// TS port of the backend matcher `PyloRelationPathMatcher::grantsField`
// (backend/app/app/Services/PyloRelationPathMatcher.php). Keep the two in sync.
//
// `field_paths` patterns are dot-separated paths relative to the permission's entity: the LAST
// token is the field selector, every preceding token is a relation hop. `*` matches any field /
// relation at its position. A `!`-prefixed pattern is a NEGATION (exclusion); a negation also
// implies an all-`*` baseline at its depth (so `!id` means "all base fields except id").
//
//   test          -> field `test` on the base entity
//   *             -> all fields of the base entity
//   !id           -> all base fields EXCEPT `id`
//   rel.*         -> all fields of relation `rel`
//   *.*           -> all fields of every direct relation (NOT the base entity's own fields)
//
// A field is granted when matched by a positive pattern (or by the implied baseline of a negation
// at the same depth) AND not matched by any negation.

interface SplitPatterns {
  positive: string[][];
  negative: string[][];
}

function splitPatterns(patterns: string[]): SplitPatterns {
  const positive: string[][] = [];
  const negative: string[][] = [];
  for (const raw of patterns) {
    if (typeof raw !== "string") continue;
    let pattern = raw.trim();
    const isNegative = pattern.startsWith("!");
    if (isNegative) pattern = pattern.slice(1).trim();
    const tokens = pattern.split(".");
    if (isNegative) negative.push(tokens);
    else positive.push(tokens);
  }
  return { positive, negative };
}

// Does each relation hop in `relationPath` match the corresponding pattern token (`*` matches any)?
function relationPrefixMatches(tokens: string[], relationPath: string[]): boolean {
  return relationPath.every((hop, i) => tokens[i] === "*" || tokens[i] === hop);
}

// Does `tokens` grant `field` exactly `depth` hops deep along `relationPath`?
function fieldPatternMatches(
  tokens: string[],
  relationPath: string[],
  depth: number,
  field: string,
): boolean {
  if (tokens.length !== depth + 1) return false;
  if (!relationPrefixMatches(tokens, relationPath)) return false;
  const fieldToken = tokens[depth];
  return fieldToken === "*" || fieldToken === field;
}

/**
 * Is reading/writing `field` on the entity reached after traversing `relationPath` (relative to the
 * permission's entity; `[]` means the entity itself) granted by `patterns`?
 */
export function grantsField(patterns: string[], relationPath: string[], field: string): boolean {
  const depth = relationPath.length;
  const { positive, negative } = splitPatterns(patterns);

  // a negation at this depth implies an all-`*` baseline (e.g. `!id` => all base fields)
  const impliedBaseline = negative.some((tokens) => tokens.length === depth + 1);

  let granted = impliedBaseline;
  if (!granted) {
    granted = positive.some((tokens) => fieldPatternMatches(tokens, relationPath, depth, field));
  }
  if (!granted) return false;

  // an exclusion wins over any grant
  for (const tokens of negative) {
    if (fieldPatternMatches(tokens, relationPath, depth, field)) return false;
  }
  return true;
}
